import { useEffect, useState } from 'react';
import type { CustomerCard as Card, CustomerCreate } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate, formatMoney } from '../format';
import { DebtPayment } from './DebtPayment';
import { BonusPanel } from './BonusPanel';
import { moveMark, moveTitle } from '../customerMoves';
import { useConfirm } from '../components/ConfirmDialog';

interface Props {
  id: string | null;
  canEdit: boolean;
  /** customer.payment: ödənişi ləğv etmək (KT-46). */
  canPay?: boolean;
  /** bonus.adjust: bonusu əl ilə düzəltmək (KT-96). */
  canAdjustBonus?: boolean;
  onBack: () => void;
  onSaved: (id: string) => void;
}

// Müştəri kartı. Yalnız oxu üçün nisyə balansı və hərəkətlər; redaktə yalnız customer.edit ilə.
export function CustomerCard({ id, canEdit, canPay = false, canAdjustBonus = false, onBack, onSaved }: Props) {
  const confirm = useConfirm();
  const [card, setCard] = useState<Card | null>(null);
  const [draft, setDraft] = useState<CustomerCreate>({ name: '' });
  const [editing, setEditing] = useState(id === null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!id) return;
    api
      .customerCard(id)
      .then((c) => {
        setCard(c);
        setDraft({
          name: c.name,
          kind: c.kind,
          voen: c.voen,
          phone: c.phone,
          address: c.address,
          creditLimit: c.creditLimit,
          note: c.note,
        });
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası'));
  }, [id, reload]);

  async function reversePayment(paymentId: string, title: string) {
    if (!(await confirm(`«${title}» ödənişi ləğv olunsun? Əks sətir yazılacaq, borc geri qayıdacaq.`, { confirmLabel: 'Ləğv et', danger: true }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.debtReverse(paymentId, crypto.randomUUID());
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ləğv alınmadı');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const saved = id === null ? await api.customerCreate(draft) : await api.customerPatch(id, draft);
      onSaved(saved.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası');
    } finally {
      setBusy(false);
    }
  }

  const set = <K extends keyof CustomerCreate>(key: K, value: CustomerCreate[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn" onClick={onBack}>
          ← Müştərilər
        </button>
        <span className="brand">{id === null ? 'Yeni müştəri' : (card?.name ?? 'Müştəri')}</span>
      </header>

      <main className="content">
        <section className="card card-wide">
          <h2>{card?.name ?? 'Yeni müştəri'}</h2>
          {error && <p className="bad">{error}</p>}

          {editing && canEdit ? (
            <div className="product-form">
              <label className="field">
                <span>Ad</span>
                <input value={draft.name} onChange={(e) => set('name', e.target.value)} />
              </label>
              <label className="field">
                <span>Telefon</span>
                <input value={draft.phone ?? ''} onChange={(e) => set('phone', e.target.value || null)} />
              </label>
              <label className="field">
                <span>VÖEN</span>
                <input value={draft.voen ?? ''} onChange={(e) => set('voen', e.target.value || null)} />
              </label>
              <label className="field">
                <span>Ünvan</span>
                <input value={draft.address ?? ''} onChange={(e) => set('address', e.target.value || null)} />
              </label>
              <label className="field">
                <span>Nisyə limiti, ₼</span>
                <input
                  value={draft.creditLimit ?? ''}
                  onChange={(e) => set('creditLimit', e.target.value === '' ? null : Number(e.target.value))}
                />
              </label>
              <label className="field">
                <span>Qeyd</span>
                <input value={draft.note ?? ''} onChange={(e) => set('note', e.target.value || null)} />
              </label>
              <div className="form-actions">
                <button type="button" className="btn-primary" disabled={busy || !draft.name.trim()} onClick={() => void save()}>
                  Yadda saxla
                </button>
                {id !== null && (
                  <button type="button" className="btn-small" onClick={() => setEditing(false)}>
                    Ləğv et
                  </button>
                )}
              </div>
            </div>
          ) : (
            card && (
              <>
                <dl className="facts">
                  <dt>Telefon</dt>
                  <dd>{card.phone ?? '—'}</dd>
                  <dt>VÖEN</dt>
                  <dd>{card.voen ?? '—'}</dd>
                  <dt>Ünvan</dt>
                  <dd>{card.address ?? '—'}</dd>
                  <dt>Nisyə limiti</dt>
                  <dd>{card.creditLimit === null ? 'limitsiz' : formatMoney(card.creditLimit)}</dd>
                  <dt>Qeyd</dt>
                  <dd>{card.note ?? '—'}</dd>
                </dl>
                <p>
                  Cari borc: <strong className={card.balance > 0 ? 'bad' : ''}>{formatMoney(card.balance)}</strong>
                  {card.unconfirmed > 0 && (
                    <span className="muted"> (təsdiq gözləyən: {formatMoney(card.unconfirmed)})</span>
                  )}
                </p>
                {canEdit && (
                  <button type="button" className="btn-small" onClick={() => setEditing(true)}>
                    Dəyiş
                  </button>
                )}
              </>
            )
          )}
        </section>

        {id && card && <DebtPayment customerId={id} balance={card.balance} onPaid={() => setReload((n) => n + 1)} />}

        {id && card && <BonusPanel key={`${id}-${reload}`} customerId={id} canAdjust={canAdjustBonus} />}

        {card && card.moves.length > 0 && (
          <section className="card card-wide">
            <h2>Hərəkətlər</h2>
            <div className="product-list">
              {card.moves.map((m) => (
                <div key={m.id} className="product-row">
                  <span className="muted small">{formatDate(m.movedAt)}</span>
                  <span>{moveTitle(m)}</span>
                  <span className={m.amount > 0 ? 'bad' : 'ok'}>{formatMoney(m.amount)}</span>
                  {m.pending && <span className="tag">təsdiq gözləyir</span>}
                  {moveMark(m).label && <span className="tag">{moveMark(m).label}</span>}
                  {canPay && moveMark(m).canReverse && m.paymentId && (
                    <button type="button" className="link-btn" disabled={busy} onClick={() => reversePayment(m.paymentId!, moveTitle(m))}>
                      Ləğv et
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
